import React, { useEffect, useState } from "react";
import FilterBar from "./filterBar";
import { AnimatePresence, motion } from "framer-motion";
import { Input } from "components/ui";
import { FaFilter } from "react-icons/fa";

const Header = ({
  search,
  setSearch,
  setFilterValue,
  filterValue,
  FilterMenu,
  placeholder,
  rangeData,
  showMoreFilters,
  showDropdownBtn,
  totalCount,
  broadcastingValue,
  setBroadcastingValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showError, setShowError] = useState({
    maxInhand: "",
    maxExpected: "",
  });

  // Validate the filter values whenever they change
  useEffect(() => {
    const validateFilters = () => {
      const errors = {
        maxInhand: "",
        maxExpected: "",
        maxExp: "",
        maxJeweleryExp: "",
      };

      // Only validate if both min and max values exist for each range

      if (
        filterValue?.minExp !== undefined &&
        filterValue?.maxExp !== undefined
      ) {
        const minExp = Number(filterValue.minExp);
        const maxExp = Number(filterValue.maxExp);

        if (minExp > maxExp && minExp !== 0 && maxExp !== 0) {
          errors.maxExp = "Min Exp cannot be greater than Max Exp";
        }
      }

      if (
        filterValue?.minJeweleryExp !== undefined &&
        filterValue?.maxJeweleryExp !== undefined
      ) {
        const minJeweleryExp = Number(filterValue.minJeweleryExp);
        const maxJeweleryExp = Number(filterValue.maxJeweleryExp);

        if (
          minJeweleryExp > maxJeweleryExp &&
          minJeweleryExp !== 0 &&
          maxJeweleryExp !== 0
        ) {
          errors.maxJeweleryExp =
            "Min Jewelery Exp cannot be greater than Max Jewelery Exp";
        }
      }
      if (
        filterValue?.minInhand !== undefined &&
        filterValue?.maxInhand !== undefined
      ) {
        const minInhand = Number(filterValue.minInhand);
        const maxInhand = Number(filterValue.maxInhand);

        if (minInhand > maxInhand && minInhand !== 0 && maxInhand !== 0) {
          errors.maxInhand = "Min Inhand cannot be greater than Max Inhand";
        }
      }

      if (
        filterValue?.minExpected !== undefined &&
        filterValue?.maxExpected !== undefined
      ) {
        const minExpected = Number(filterValue.minExpected);
        const maxExpected = Number(filterValue.maxExpected);

        if (
          minExpected > maxExpected &&
          minExpected !== 0 &&
          maxExpected !== 0
        ) {
          errors.maxExpected =
            "Min Expected cannot be greater than Max Expected";
        }
      }

      setShowError(errors);
    };

    validateFilters();
  }, [filterValue, isOpen]); // Run when filterValue or isOpen changes

  return (
    <div className="flex flex-col">
      <FilterBar
        search={search}
        FilterMenu={FilterMenu}
        setSearch={setSearch}
        placeholder={placeholder}
        setFilterValue={setFilterValue}
        filterValue={filterValue}
        rangeData={rangeData}
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        showMoreFilters={showMoreFilters}
        showDropdownBtn={showDropdownBtn}
        totalCount={totalCount}
        broadcastingValue={broadcastingValue}
        setBroadcastingValue={setBroadcastingValue}
      />

      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="animated-box"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut",
            }}
            className="mt-4 p-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Inhand Salary Section */}
              <div className="flex flex-col gap-3">
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Inhand Salary
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Minimum
                    </label>
                    <Input
                      name="minInhand"
                      type="text"
                      placeholder="0"
                      value={filterValue?.minInhand || ""}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ""
                        ) {
                          setFilterValue({
                            ...filterValue,
                            minInhand: e.target.value,
                          });
                        }
                      }}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Maximum
                    </label>
                    <Input
                      name="maxInhand"
                      type="text"
                      placeholder="0"
                      value={filterValue?.maxInhand || ""}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ""
                        ) {
                          setFilterValue({
                            ...filterValue,
                            maxInhand: e.target.value,
                          });
                        }
                      }}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                    {showError.maxInhand && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {showError.maxInhand}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Expected Salary Section */}
              <div className="flex flex-col gap-3">
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Expected Salary
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Minimum
                    </label>
                    <Input
                      name="minExpected"
                      type="text"
                      placeholder="0"
                      value={filterValue?.minExpected || ""}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ""
                        ) {
                          setFilterValue({
                            ...filterValue,
                            minExpected: e.target.value,
                          });
                        }
                      }}
                      className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Maximum
                    </label>
                    <Input
                      name="maxExpected"
                      type="text"
                      placeholder="0"
                      value={filterValue?.maxExpected || ""}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ""
                        ) {
                          setFilterValue({
                            ...filterValue,
                            maxExpected: e.target.value,
                          });
                        }
                      }}
                      className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                    />
                    {showError.maxExpected && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {showError.maxExpected}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Inhand Salary Section */}
              <div className="flex flex-col gap-3">
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  Total Experience
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Minimum
                    </label>
                    <Input
                      name="minExp"
                      type="text"
                      placeholder="0"
                      value={filterValue?.minExp || ""}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ""
                        ) {
                          setFilterValue({
                            ...filterValue,
                            minExp: e.target.value,
                          });
                        }
                      }}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Maximum
                    </label>
                    <Input
                      name="maxExp"
                      type="text"
                      placeholder="0"
                      value={filterValue?.maxExp || ""}
                      onChange={(e) => {
                        if (
                          /^[0-9]*\.?[0-9]*$/.test(e.target.value) ||
                          e.target.value === ""
                        ) {
                          setFilterValue({
                            ...filterValue,
                            maxExp: e.target.value,
                          });
                        }
                      }}
                      className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                    {showError.maxExp && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {showError.maxExp}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Expected Salary Section */}
              <div className="flex flex-col gap-3">
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Jewelery Experience
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Minimum
                    </label>
                    <Input
                      name="minJeweleryExp"
                      type="text"
                      placeholder="0"
                      value={filterValue?.minJeweleryExp || ""}
                      onChange={(e) =>
                        setFilterValue({
                          ...filterValue,
                          minJeweleryExp: e.target.value,
                        })
                      }
                      className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Maximum
                    </label>
                    <Input
                      name="maxJeweleryExp"
                      type="text"
                      placeholder="0"
                      value={filterValue?.maxJeweleryExp || ""}
                      onChange={(e) =>
                        setFilterValue({
                          ...filterValue,
                          maxJeweleryExp: e.target.value,
                        })
                      }
                      className="border-gray-300 focus:border-green-500 focus:ring-green-500"
                    />
                    {showError.maxJeweleryExp && (
                      <span className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {showError.maxJeweleryExp}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Header;
